import { usePostsFeatured } from '@gik/api/posts/postsFeatured';
import Link from '@gik/core/components/Link.tsx';
import routes from '@gik/core/routes';
import bemBlock from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import { CreateClickedLocation } from '@gik/create/enums/CreateAnalyticsEnums';
import { useCreateAnalyticsController } from '@gik/create/hooks/useCreateAnalyticsController';
import { Grid } from '@gik/ui-legacy/Grid';
import { HoverAnimation } from '@gik/ui-legacy/HoverAnimation';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import FacebookIconWhite from '../assets/facebook.svg';
import GikLogoWhite from '../assets/gik-logo-white.svg';
import InstagramIconWhite from '../assets/instagram.svg';
import PinterestIconWhite from '../assets/pinterest.svg';
import TwitterIconWhite from '../assets/twitter.svg';
import YoutubeIconWhite from '../assets/youtube.svg';
import { translationKeys } from './i18n/en';

const bem = bemBlock('app-footer');

interface FooterLink {
  title: JSX.Element | string;
  url: string;
  scroll?: boolean;
  trackingFunction?: () => void;
}

interface FooterLinkListProps {
  title: string;
  links: FooterLink[];
}

function FooterLinkList({ title, links }: FooterLinkListProps) {
  return (
    <div className={bem('grid-link-list')}>
      <p>{title}</p>
      {links.map(({ title, url, trackingFunction = noop }) => (
        <Link key={url} href={url} className={bem('grid-link')}>
          <p onClick={trackingFunction}>{title}</p>
        </Link>
      ))}
    </div>
  );
}

interface SocialMediaLink {
  icon: React.ReactNode;
  url: string;
  ariaLabel: string;
}

const socialMediaLinks: SocialMediaLink[] = [
  {
    icon: <FacebookIconWhite />,
    url: 'https://www.facebook.com/giveinkind/',
    ariaLabel: 'Visit our Facebook page',
  },
  {
    icon: <TwitterIconWhite />,
    url: 'https://twitter.com/giveinkind',
    ariaLabel: 'Visit our Twitter page',
  },
  {
    icon: <InstagramIconWhite />,
    url: 'https://www.instagram.com/giveinkind/',
    ariaLabel: 'Visit our Instagram page',
  },
  {
    icon: <PinterestIconWhite />,
    url: 'https://www.pinterest.com/giveinkind',
    ariaLabel: 'Visit our Pinterest page',
  },
  {
    icon: <YoutubeIconWhite />,
    url: 'https://www.youtube.com/channel/UCGp15GUbx5bFwxQwAQUpnRw',
    ariaLabel: 'Visit our YouTube channel',
  },
];

export function FinePrintLink({ title, url }: FooterLink) {
  return <Link href={url}>{title}</Link>;
}

export function AppFooter() {
  const { t } = useTranslation();

  const { trackCreateClicked } = useCreateAnalyticsController();
  const posts = usePostsFeatured({
    slug: 'footer-help',
    perPage: 3,
    fields: ['title', 'slug'],
  });

  const footerLinkLists: FooterLinkListProps[] = [
    {
      title: t(translationKeys.inkind),
      links: [
        { title: <span>{t(translationKeys.about)}</span>, url: routes.about },
        { title: <span>{t(translationKeys.contact)}</span>, url: routes.contact },
        { title: <span>{t(translationKeys.support)}</span>, url: routes.support },
        {
          title: <span>{t(translationKeys.press)}</span>,
          url: 'https://create.giveinkind.com/press/',
        },
        {
          title: 'Buy Bulk Gift Cards',
          url: 'https://www.perfectgift.com/lp/giveinkind',
        },
        // { title: <span>{t(translationKeys.write)}</span>, url: 'https://create.giveinkind.com/call-for-submissions/' },
        // { title: <span>{t(translationKeys.healthCare)}</span>, url: routes.healthcare }, //GIK-4713
      ],
    },
    {
      title: t(translationKeys.inkindPages),
      links: [
        {
          title: <span>{t(translationKeys.create)}</span>,
          url: routes.create(),
          trackingFunction: () => trackCreateClicked(CreateClickedLocation.PAGE_FOOTER),
        },
        { title: <span>{t(translationKeys.howItWorks)}</span>, url: routes.howItWork },
        { title: <span>{t(translationKeys.features)}</span>, url: routes.features },
        { title: <span>{t(translationKeys.compare)}</span>, url: routes.competitors },
        { title: <span>{t(translationKeys.faq)}</span>, url: routes.commonQuestions },
      ],
    },
    {
      title: <span>{t(translationKeys.howCanIHelp)}</span>,
      links: [
        {
          title: <span>{t(translationKeys.browseSituations)}</span>,
          url: routes.situation,
          scroll: false,
        },
        { title: <span>{t(translationKeys.blog)}</span>, url: routes.blog },
        ...(posts?.data?.map(p => ({
          title: <span>{p.title.rendered}</span>,
          url: `${routes.blog}/${p.slug}`,
        })) || []),
      ],
    },
  ];

  const finePrint = (
    <div className={bem('fine-print')}>
      <p>
        {/* TODO: migrate to use LegalTextModal */}
        <FinePrintLink title={<span>{t(translationKeys.tos)}</span>} url={routes.termsService} /> •&nbsp;
        <FinePrintLink title={<span>{t(translationKeys.privacy)}</span>} url={routes.privacyPolicy} /> •&nbsp;
        <FinePrintLink title={<span>{t(translationKeys.dpa)}</span>} url={routes.dataProcessingAgreement} /> •&nbsp;
        <FinePrintLink title={<span>{t(translationKeys.sitemap)}</span>} url={routes.sitemap} />
        {/* TODO: attribution - hidden for now*/
        /*<span className="gik-hidden-lg-down"> • </span>
        <FinePrintLink title={t(translationKeys.attribution)} url="/attribution" />*/}
      </p>
      <div>
        <div className={bem('copyright')}>&copy;{new Date().getFullYear()} Wolfe, LLC. All Rights Reserved.</div>
        <p>495 Mansfield Blvd, Pittsburgh, PA 15205</p>
        <a href="https://www.wolfe.com">www.wolfe.com</a>
      </div>
    </div>
  );

  return (
    <footer className={bem()}>
      <Grid className={bem('links-grid')}>
        {footerLinkLists.map(list => (
          <FooterLinkList key={list.title} {...list} />
        ))}
      </Grid>
      <div className={bem('right')}>
        <div className={bem('heading')}>
          <GikLogoWhite />

          <div className={bem('social-links')}>
            {socialMediaLinks.map(link => (
              <a key={link.url} href={link.url} target="_blank" rel="noreferrer" aria-label={link.ariaLabel}>
                <HoverAnimation>
                  <div className={bem('social-link')}>{link.icon}</div>
                </HoverAnimation>
              </a>
            ))}
          </div>
        </div>
      </div>
      {finePrint}
    </footer>
  );
}
